import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import DescargaFormatoModal from "./DescargaFormatoModal";

/**
 * PDFExportButton — Botón único, reutilizable, para exportar reportes en PDF.
 * Solo se muestra a directores.
 *
 * Props:
 *   endpoint (string, required): ruta relativa dentro de /api/reportes-pdf/ (ej. "clientes", "pedidos")
 *   filename (string, required): nombre sugerido para el archivo
 *   label (string, optional): texto del botón (default "Exportar PDF")
 *   fields (array, optional): campos que necesita pedir antes de generar. Cada uno: {key, label, type, default, options}
 *     type: "date" | "select" | "text"
 *     options: [{value, label}] cuando type=select
 *   extraParams (object, optional): querystring fijo que se manda siempre
 *   testid (string, optional): data-testid del botón principal
 */
export default function PDFExportButton({ endpoint, filename, label = "Exportar PDF", fields = [], extraParams = {}, testid, openSignal = 0, hidden = false }) {
  const { user } = useAuth();
  const isDirector = user?.role === "director" || user?.role === "admin";
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(() => Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "checkbox" ? false : "")])));
  const [loading, setLoading] = useState(false);
  const [dynamicOptions, setDynamicOptions] = useState({});
  // Selector de formato PDF vs Imagen — se abre despues del formulario
  const [formatoOpen, setFormatoOpen] = useState(false);
  const [paramsPendientes, setParamsPendientes] = useState(null);

  // Disparo externo (desde un menú "Exportar ▾"): cada cambio de openSignal abre el flujo
  useEffect(() => {
    if (!openSignal || !isDirector) return;
    if (fields.length === 0) { setParamsPendientes({}); setFormatoOpen(true); } else setOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  // Precarga opciones dinámicas cuando se abre el modal (una sola vez)
  useEffect(() => {
    if (!open) return;
    const need = fields.some((f) => f.optionsFrom);
    if (!need || Object.keys(dynamicOptions).length > 0) return;
    api.get("/reportes-pdf/opciones").then(({ data }) => setDynamicOptions(data || {})).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!isDirector) return null;

  const trigger = async () => {
    if (fields.length === 0) {
      // Preguntar formato directamente
      setParamsPendientes({});
      setFormatoOpen(true);
    } else {
      setOpen(true);
    }
  };

  const doExport = async (params, format = "pdf") => {
    setLoading(true);
    try {
      const cleanParams = {};
      Object.entries({ ...extraParams, ...params }).forEach(([k, v]) => {
        if (v === undefined || v === null || v === "" || v === false) return;
        cleanParams[k] = v === true ? "true" : v;
      });
      if (format === "png") cleanParams.format = "png";
      if (format === "xlsx") cleanParams.format = "xlsx";
      const res = await api.get(`/reportes-pdf/${endpoint}`, {
        params: cleanParams,
        responseType: "blob",
      });
      const isPng = format === "png";
      const isXlsx = format === "xlsx";
      const type = isPng
        ? "image/png"
        : isXlsx
          ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          : "application/pdf";
      const ext = isPng ? "png" : isXlsx ? "xlsx" : "pdf";
      const blob = new Blob([res.data], { type });
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safeName = filename.replace(/\.(pdf|png|xlsx)$/i, "");
      a.href = blobUrl;
      a.download = `${safeName}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Solo abrir en pestaña nueva PDF/PNG — el xlsx se descarga sin preview.
      if (!isXlsx) {
        window.open(blobUrl, "_blank");
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      toast.success(
        isPng ? "Imagen generada" : isXlsx ? "Excel generado" : "PDF generado"
      );
      setOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.message || "";
      toast.error(`No se pudo generar${detail ? ": " + detail : ""}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!hidden && (
      <Button
        onClick={trigger}
        variant="outline"
        size="sm"
        className="gap-1.5"
        disabled={loading}
        data-testid={testid || `export-pdf-${endpoint}`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
        {label}
      </Button>
      )}

      {fields.length > 0 && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{label}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground -mt-1">
                Deja los campos vacíos para incluir todo. Aplica los filtros que necesites.
              </p>
              {fields.map((f) => {
                const opts = f.optionsFrom ? (dynamicOptions[f.optionsFrom] || []) : (f.options || []);
                if (f.type === "checkbox") {
                  return (
                    <label key={f.key} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={!!values[f.key]}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.checked })}
                        data-testid={`export-field-${f.key}`}
                      />
                      <span>{f.label}</span>
                    </label>
                  );
                }
                return (
                  <div key={f.key}>
                    <Label className="text-xs text-muted-foreground">{f.label}</Label>
                    {f.type === "select" ? (
                      <select
                        value={values[f.key] || ""}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        className="w-full mt-1 border border-border rounded-md h-9 px-2 text-sm bg-background"
                        data-testid={`export-field-${f.key}`}
                      >
                        <option value="">— Todos —</option>
                        {opts.map((o) => (
                          <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                      </select>
                    ) : (
                      <Input
                        type={f.type || "text"}
                        value={values[f.key] || ""}
                        onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                        data-testid={`export-field-${f.key}`}
                      />
                    )}
                  </div>
                );
              })}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button
                  onClick={() => { setParamsPendientes(values); setFormatoOpen(true); }}
                  disabled={loading}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="export-pdf-confirm"
                >
                  Siguiente
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <DescargaFormatoModal
        open={formatoOpen}
        onOpenChange={setFormatoOpen}
        titulo={label}
        onElegir={async (format) => {
          await doExport(paramsPendientes || {}, format);
          setParamsPendientes(null);
        }}
      />
    </>
  );
}
